import { Box, Button, Chip, List, Stack, styled, Tooltip, Typography } from "@mui/material";
import { type IDatePresetRangeFilterValue, type IFilterOption } from "Common/Interfaces";
import React, { useEffect } from "react";
import { FilterCheckboxItem } from "./FilterCheckboxItem";
import dayjs from "dayjs";
import { FILTER_OPTION_TYPE } from "Common/Constants";
import { PopperWrapper } from "../Wrappers";
import { SearchBox } from "../SearchBox";
import { FilterDatePresetRangePicker } from "./FilterDatePresetRangePicker";
import { DATE_PRESET_OPTIONS } from "./Constants";
import { isDateRangeValueValid } from "./Functions";
import { FilterDateTimeRangePicker } from "./FilterDateTimeRangePicker";

interface IFilterChipListProps {
  filterState: IFilterOption[];
  handleFilterState: (filterState: IFilterOption[], removeFilter?: boolean) => void;
}

const FilterChip = styled(Chip)(({ theme }) => ({
  background: theme.palette.common.white,
  border: `1px solid ${theme.palette.grey[300]}`,

  "&:hover": {
    border: `1px solid ${theme.palette.grey[400]}`,
    background: theme.palette.grey[100],
  },
  ".MuiChip-deleteIcon": {
    color: theme.palette.secondary.light,
    "&:hover": {
      color: theme.palette.secondary.main,
    },
  },
}));

const formatDateRangeLabel = (filterOption: IFilterOption): string => {
  const { startDate, endDate } = filterOption.dateRangeValues!;
  const dateFormat = filterOption.type === FILTER_OPTION_TYPE.DATE_TIME_RANGE ? "MM/DD/YYYY hh:mm A" : "MM/DD/YYYY";
  const from = startDate ? dayjs(startDate).format(dateFormat) : "-";
  const to = endDate ? dayjs(endDate).format(dateFormat) : "-";
  return `${from}, ${to}`;
};

const formatDatePresetRangeLabel = (filterOption: IFilterOption): string => {
  const { selectedPreset, customStartDate, customEndDate } = filterOption.datePresetRangeValues!;
  if (selectedPreset === "custom") {
    const fmt = "MM/DD/YYYY hh:mm A";
    const from = customStartDate ? dayjs(customStartDate).format(fmt) : "-";
    const to = customEndDate ? dayjs(customEndDate).format(fmt) : "-";
    return `${from} - ${to}`;
  }
  return DATE_PRESET_OPTIONS.find((p) => p.value === selectedPreset)?.label ?? selectedPreset ?? "";
};

const getChipLabelValue = (filterOption: IFilterOption): string => {
  if (filterOption.checkboxValues) {
    return filterOption.checkboxValues
      .filter((value) => value.isChecked)
      .map((value) => value.label)
      .join(", ");
  }
  if (filterOption.autoCompleteValues?.value) {
    return filterOption.autoCompleteValues.value.label;
  }
  if (filterOption.multipleAutocompleteValues?.value) {
    return filterOption.multipleAutocompleteValues.value.map((value) => value.label).join(", ");
  }
  if (filterOption.freeTextValues?.value) {
    return filterOption.freeTextValues.value;
  }
  if (filterOption.dateRangeValues) {
    return formatDateRangeLabel(filterOption);
  }
  if (filterOption.datePresetRangeValues?.selectedPreset) {
    return formatDatePresetRangeLabel(filterOption);
  }
  if (filterOption.autoCompleteWithCheckboxValues) {
    if (filterOption.autoCompleteWithCheckboxValues.checkboxValue?.isChecked) {
      return filterOption.autoCompleteWithCheckboxValues.checkboxValue.label;
    }
    if (filterOption.autoCompleteWithCheckboxValues.value) {
      return filterOption.autoCompleteWithCheckboxValues.value.value.toString();
    }
  }
  return "";
};

export const FilterChipList = (props: IFilterChipListProps) => {
  const { filterState, handleFilterState } = props;
  const [isDatePresetRangeValid, setIsDatePresetRangeValid] = React.useState(true);

  const [filterMenu, setFilterMenu] = React.useState<{
    open: boolean;
    anchorEl: null | HTMLElement;
    filterOption: IFilterOption | null;
  }>({
    open: false,
    anchorEl: null,
    filterOption: null,
  });

  const isDatePresetRangeValueValid = (filterOption?: IFilterOption | null) => {
    const preset = filterOption?.datePresetRangeValues;
    if (filterOption?.type !== FILTER_OPTION_TYPE.DATE_PRESET_RANGE || preset?.selectedPreset !== "custom") return true;
    return isDateRangeValueValid(preset.customStartDate, preset.customEndDate);
  };

  const isEmptyCustomDatePreset = (filterOption?: IFilterOption | null) =>
    filterOption?.type === FILTER_OPTION_TYPE.DATE_PRESET_RANGE &&
    filterOption.datePresetRangeValues?.selectedPreset === "custom" &&
    !filterOption.datePresetRangeValues.customStartDate &&
    !filterOption.datePresetRangeValues.customEndDate;

  const handleClickOnChip = (e: React.MouseEvent<HTMLDivElement, MouseEvent>, filterOption: IFilterOption) => {
    setIsDatePresetRangeValid(true);
    setFilterMenu({
      open: true,
      anchorEl: e.currentTarget,
      filterOption,
    });
  };

  const handleCloseMenu = (event: Event | React.SyntheticEvent) => {
    if (filterMenu.anchorEl?.contains(event.target as HTMLElement)) {
      return;
    }
    setIsDatePresetRangeValid(true);
    setFilterMenu((prev) => ({
      ...prev,
      anchorEl: null,
      filterOption: null,
      open: false,
    }));
  };

  // #region Checkbox handling
  const [searchText, setSearchText] = React.useState<{ [key: number | string]: string }>({});

  useEffect(() => {
    setSearchText({});
    setHasDateRangeError(false);
    setIsDatePresetRangeValid(true);
  }, [filterMenu.open]);

  const handleSearchChange = (filterOptionId: number | string, search: string) => {
    setSearchText((prev) => ({
      ...prev,
      [filterOptionId]: search,
    }));
  };

  const handleCheckboxChange = (value: number | string | boolean) => {
    setFilterMenu((prev) => ({
      ...prev,
      filterOption: {
        ...prev.filterOption!,
        checkboxValues: prev.filterOption?.checkboxValues?.map((filterValue) => {
          if (filterValue.value.toString() === value.toString()) {
            return {
              ...filterValue,
              isChecked: !filterValue.isChecked,
            };
          }
          return filterValue;
        }),
      },
    }));
  };

  //Handle date range selection
  const [hasDateRangeError, setHasDateRangeError] = React.useState<boolean>(false);

  // Handle Autocomplete of Autocomplete with checkbox

  // Handle Checkbox of Autocomplete with checkbox

  const handleDatePresetRangeUpdateInChip = (updates: Partial<IDatePresetRangeFilterValue>) => {
    if (!filterMenu.filterOption?.datePresetRangeValues) return;
    setFilterMenu((prev) => ({
      ...prev,
      filterOption: {
        ...prev.filterOption!,
        datePresetRangeValues: { ...prev.filterOption!.datePresetRangeValues!, ...updates },
      },
    }));
  };

  const getFilterValuesBasedOnSearch = (filterOptionId: number | string) => {
    const search = searchText[filterOptionId]?.toLowerCase() || "";

    const filterOption = filterMenu.filterOption;

    if (filterOption?.type === FILTER_OPTION_TYPE.CHECKBOX && filterOption.checkboxValues) {
      const filteredCheckboxValues = filterOption.checkboxValues.filter((filterValue) =>
        filterValue.label.toLowerCase().includes(search),
      );

      return filteredCheckboxValues;
    }

    return filterOption?.checkboxValues || [];
  };
  // #end region

  function handleApplyFilters(e: React.MouseEvent<HTMLButtonElement>, filterOptionId?: number | string) {
    if (!filterOptionId) return;
    if (!isDatePresetRangeValueValid(filterMenu.filterOption) || !isDatePresetRangeValid) {
      return;
    }

    handleCloseMenu(e);

    if (isEmptyCustomDatePreset(filterMenu.filterOption)) {
      return;
    }

    handleFilterState(
      filterState.map((filterOption) => {
        if (filterOption.id === filterOptionId) {
          // If there's a date range error, keep the original filter option unchanged
          if (hasDateRangeError && filterOption.type === FILTER_OPTION_TYPE.DATE_RANGE) {
            setHasDateRangeError(false);
            return filterOption;
          }

          const updatedFilterOption = { ...filterMenu.filterOption! };
          return updatedFilterOption;
        }
        return filterOption;
      }),
    );
  }

  const handleRemoveFilter = (_e: React.MouseEvent<HTMLButtonElement>, filterOptionId?: number | string) => {
    if (!filterOptionId) return;
    setFilterMenu((prev) => ({
      ...prev,
      anchorEl: null,
      filterOption: null,
      open: false,
    }));
    const updatedFilterState = filterState.map((filterOption) => {
      if (filterOption.id === filterOptionId) {
        switch (filterOption.type) {
          case FILTER_OPTION_TYPE.CHECKBOX:
            return {
              ...filterOption,
              checkboxValues: filterOption.checkboxValues?.map((filterValue) => ({
                ...filterValue,
                isChecked: false,
              })),
            };

          case FILTER_OPTION_TYPE.DATE_RANGE:
          case FILTER_OPTION_TYPE.DATE_TIME_RANGE:
            return {
              ...filterOption,
              dateRangeValues: {
                startDate: null,
                endDate: null,
              },
            };
          case FILTER_OPTION_TYPE.DATE_PRESET_RANGE:
            return {
              ...filterOption,
              datePresetRangeValues: {
                selectedPreset: null,
                customStartDate: null,
                customEndDate: null,
              },
            };
          case FILTER_OPTION_TYPE.FREE_TEXT:
            return {
              ...filterOption,
              freeTextValues: {
                value: "",
              },
            };
          case FILTER_OPTION_TYPE.AUTO_COMPLETE:
            return {
              ...filterOption,
              autoCompleteValues: {
                ...filterOption.autoCompleteValues,
                value: null,
              },
            };
          case FILTER_OPTION_TYPE.MULTIPLE_AUTOCOMPLETE:
            return {
              ...filterOption,
              multipleAutocompleteValues: {
                ...filterOption.multipleAutocompleteValues,
                value: [],
              },
            };
          case FILTER_OPTION_TYPE.AUTOCOMPLETE_WITH_CHECKBOX:
            return {
              ...filterOption,
              autoCompleteWithCheckboxValues: {
                ...filterOption.autoCompleteWithCheckboxValues,
                value: null,
                checkboxValue: {
                  ...filterOption.autoCompleteWithCheckboxValues?.checkboxValue,
                  isChecked: false,
                },
              },
            };
        }
      }
      return filterOption;
    });

    handleFilterState(updatedFilterState as IFilterOption[], true);
  };

  return (
    <React.Fragment>
      <Stack
        sx={{
          display: "flex",
          flexDirection: "row",
          gap: 1,
          overflowX: "auto",
          paddingBottom: 1,
        }}
      >
        {filterState
          .filter(
            (filterOption) =>
              filterOption.checkboxValues?.some((value) => value.isChecked) ||
              (filterOption.autoCompleteValues?.value !== null && filterOption.autoCompleteValues?.value !== undefined) ||
              ((filterOption.multipleAutocompleteValues?.value.length ?? 0) > 0 &&
                filterOption.multipleAutocompleteValues?.value !== undefined) ||
              (filterOption.dateRangeValues?.startDate !== null && filterOption.dateRangeValues?.startDate !== undefined) ||
              (filterOption.dateRangeValues?.endDate !== null && filterOption.dateRangeValues?.endDate !== undefined) ||
              (filterOption.autoCompleteWithCheckboxValues?.value !== null &&
                filterOption.autoCompleteWithCheckboxValues?.value !== undefined) ||
              filterOption.autoCompleteWithCheckboxValues?.checkboxValue?.isChecked === true ||
              (filterOption.datePresetRangeValues?.selectedPreset !== null &&
                filterOption.datePresetRangeValues?.selectedPreset !== undefined),
          )
          .map((filterOption) => {
            const filterValues = getChipLabelValue(filterOption);

            return (
              <Tooltip key={filterOption.id} title={"Click to expand and manage applied filters"}>
                <FilterChip
                  key={filterOption.id}
                  size="small"
                  label={
                    <Stack
                      spacing={0.5}
                      sx={{
                        flexDirection: "row",
                        alignItems: "center",
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: "fontWeightSemiBold" }} component={"b"}>
                        {filterOption.label}
                      </Typography>
                      <span>:</span>
                      <Typography variant="body2" color="grey.600" component={"span"} noWrap>
                        {filterValues}
                      </Typography>
                    </Stack>
                  }
                  sx={(theme) => ({
                    maxWidth: theme.spacing(40),
                  })}
                  clickable
                  onClick={(e) => handleClickOnChip(e, filterOption)}
                  onDelete={(e) => handleRemoveFilter(e, filterOption.id)}
                />
              </Tooltip>
            );
          })}
      </Stack>

      {filterMenu.filterOption && (
        <PopperWrapper
          open={filterMenu.open}
          anchorEl={filterMenu.anchorEl}
          onClose={handleCloseMenu}
          paperProps={{
            sx: {
              padding: 0,
              minWidth: 350,
              maxWidth: 400,
            },
          }}
        >
          <Stack>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                flex: 1,
                padding: 2,
              }}
            >
              <Stack spacing={2}>
                {filterMenu.filterOption.hasSearch && (
                  <SearchBox
                    disableDebouncedSearch
                    hasBorder
                    value={searchText[filterMenu.filterOption.id] ?? ""}
                    handleSearch={(search) => handleSearchChange(filterMenu.filterOption?.id ?? 0, search)}
                    handleClear={() => handleSearchChange(filterMenu.filterOption?.id ?? 0, "")}
                  />
                )}

                {getFilterValuesBasedOnSearch(filterMenu.filterOption.id)?.length === 0 &&
                  filterMenu.filterOption?.type === FILTER_OPTION_TYPE.CHECKBOX && (
                    <Typography variant="body2" color="textSecondary">
                      No Options
                    </Typography>
                  )}

                {filterMenu.filterOption?.type === FILTER_OPTION_TYPE.CHECKBOX && (
                  <List
                    component="div"
                    sx={{
                      maxHeight: 250,
                      overflow: "auto",
                    }}
                  >
                    {getFilterValuesBasedOnSearch(filterMenu.filterOption.id)?.map((filterValue) => (
                      <FilterCheckboxItem
                        key={filterValue.value.toString()}
                        label={filterValue.label}
                        checked={filterValue.isChecked ?? false}
                        onChange={() => handleCheckboxChange(filterValue.value)}
                      />
                    ))}
                  </List>
                )}
                {filterMenu.filterOption?.type === FILTER_OPTION_TYPE.DATE_TIME_RANGE && (
                  <FilterDateTimeRangePicker
                    key={filterMenu.filterOption.id}
                    startDate={filterMenu.filterOption?.dateRangeValues?.startDate ?? null}
                    endDate={filterMenu.filterOption?.dateRangeValues?.endDate ?? null}
                    onChangeDate={(date, position) => {
                      if (!filterMenu.filterOption?.dateRangeValues) return;
                      const updatedFilterOption: IFilterOption = {
                        ...filterMenu.filterOption,
                        dateRangeValues: {
                          ...filterMenu.filterOption.dateRangeValues,
                          [position]: date,
                        },
                      };
                      setFilterMenu((prev) => ({ ...prev, filterOption: updatedFilterOption }));
                    }}
                  />
                )}
                {filterMenu.filterOption?.type === FILTER_OPTION_TYPE.DATE_PRESET_RANGE &&
                  filterMenu.filterOption.datePresetRangeValues && (
                    <FilterDatePresetRangePicker
                      key={filterMenu.filterOption.id}
                      value={filterMenu.filterOption.datePresetRangeValues}
                      onChangePreset={(preset) => {
                        if (preset === "custom") {
                          handleDatePresetRangeUpdateInChip({ selectedPreset: preset });
                          return;
                        }

                        handleDatePresetRangeUpdateInChip({
                          selectedPreset: preset,
                          customStartDate: null,
                          customEndDate: null,
                        });
                      }}
                      onChangeCustomDate={(date, position) => handleDatePresetRangeUpdateInChip({ [position]: date })}
                      onValidationChange={setIsDatePresetRangeValid}
                    />
                  )}
              </Stack>
            </Box>
            <Box
              sx={(theme) => ({
                padding: 2,
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "flex-end",
                borderTop: `1px solid ${theme.palette.divider}`,
                gap: 1,
              })}
            >
              <Button id="filter-panel-clear-button" variant="text" onClick={handleCloseMenu}>
                Cancel
              </Button>

              <Button
                id="filter-panel-apply-button"
                onClick={(e) => handleApplyFilters(e, filterMenu.filterOption?.id)}
                disabled={hasDateRangeError}
              >
                Apply
              </Button>
            </Box>
          </Stack>
        </PopperWrapper>
      )}
    </React.Fragment>
  );
};
